import { Checkbox, IconButton, MenuItem, TextField } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { ScoringCategory } from '../../../../types/advise';
import { isValidEmail } from '../../../../utils/email';
import { TextBlock } from '../../../Atoms/Text';
import { InviteeDraft } from './RequestSupplierScoring.calc';
import { INVITEE_EMAIL_LIMIT, INVITEE_NAME_LIMIT, INVITEE_ROLE_LIMIT } from './scoringRequestLimits';

interface ScoringInviteeFieldsProps {
  invitee: InviteeDraft;
  categories: ScoringCategory[];
  disabled: boolean;
  // Another row already has this address.
  repeated?: boolean;
  onChange: (patch: Partial<InviteeDraft>) => void;
  // Left out where the row can't be removed, as on a reassignment.
  onRemove?: () => void;
}

/** One invitee's name, email, role and scope; a blank scope means every category. */
export const ScoringInviteeFields = ({
  invitee,
  categories,
  disabled,
  repeated = false,
  onChange,
  onRemove
}: ScoringInviteeFieldsProps) => {
  const labelOf = (key: string) => categories.find((category) => category.key === key)?.label ?? key;
  const badEmail = !!invitee.email.trim() && !isValidEmail(invitee.email);
  const emailProblem =
    (badEmail && 'Enter a valid email address') || (repeated && 'Already on another row') || undefined;

  return (
    <div className="relative flex flex-col gap-3 rounded-lg border border-grey-2 bg-grey-1 p-3 pr-8 [&_.MuiInputBase-root]:bg-white">
      {onRemove && (
        <IconButton
          aria-label="Remove invitee"
          size="small"
          className="!absolute right-1 top-1"
          disabled={disabled}
          onClick={onRemove}>
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <TextField
          label="First name"
          required
          size="small"
          value={invitee.firstName}
          disabled={disabled}
          inputProps={{ maxLength: INVITEE_NAME_LIMIT }}
          onChange={(event) => onChange({ firstName: event.target.value })}
        />
        <TextField
          label="Last name"
          required
          size="small"
          value={invitee.lastName}
          disabled={disabled}
          inputProps={{ maxLength: INVITEE_NAME_LIMIT }}
          onChange={(event) => onChange({ lastName: event.target.value })}
        />
        <TextField
          label="Email"
          required
          type="email"
          size="small"
          value={invitee.email}
          disabled={disabled}
          error={!!emailProblem}
          helperText={emailProblem}
          inputProps={{ maxLength: INVITEE_EMAIL_LIMIT }}
          onChange={(event) => onChange({ email: event.target.value })}
        />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <TextField
          label="Role / title (optional)"
          size="small"
          value={invitee.role}
          disabled={disabled}
          inputProps={{ maxLength: INVITEE_ROLE_LIMIT }}
          onChange={(event) => onChange({ role: event.target.value })}
        />
        <TextField
          select
          label="Scope (optional)"
          size="small"
          className="sm:col-span-2"
          value={invitee.scope}
          disabled={disabled}
          InputLabelProps={{ shrink: true }}
          SelectProps={{
            multiple: true,
            displayEmpty: true,
            renderValue: (selected) => {
              const keys = selected as string[];
              return keys.length === 0 ? 'All categories' : keys.map(labelOf).join(', ');
            }
          }}
          onChange={(event) => {
            const { value } = event.target as { value: string | string[] };
            onChange({ scope: typeof value === 'string' ? value.split(',') : value });
          }}>
          {categories.map((category) => (
            <MenuItem
              key={category.key}
              value={category.key}>
              <Checkbox
                size="small"
                checked={invitee.scope.includes(category.key)}
              />
              {category.label}
            </MenuItem>
          ))}
        </TextField>
      </div>
      {invitee.error && (
        <TextBlock
          size="sm"
          color="red">
          {invitee.email.trim()}: {invitee.error}
        </TextBlock>
      )}
    </div>
  );
};
